import { useEffect, useState, useMemo } from "react";
import { FieldArray, useFormikContext, Field, FastField } from "formik";
import { TextField } from "formik-mui";

import styled from '@emotion/styled';

import { Grid, InputAdornment, Button, IconButton, Tooltip, ToggleButton, ToggleButtonGroup, Divider, Card, CardContent, useTheme } from "@mui/material";

import { useQuery } from "@tanstack/react-query";

import { getServicesByStylist, getServicesByStylists } from "../apiService";

import { getSoldInventoryByCustomer } from "../../Inventory/apiService";

import { InputLabel, BasicFlex, autoCompleteDisplayLabel, DotSeparator } from "../../../utils/Common";

import { useFormData } from "../../../contexts/FormData";

import AttachMoneyRoundedIcon from '@mui/icons-material/AttachMoneyRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';

import AutocompleteFormik from "../../../utils/FormikFields/AutocompleteDebounced";

import { addMinutes, formatCurrency, formatGenericDate } from "../../../utils/util";

import ToggleButtonComponent from "../../../utils/FormikFields/ToggleButtonComponent";

import ErrorMessage from "../../../utils/MuiFields/ErrorMessage";

import { useScheduleTypes } from "../../../contexts/ScheduleTypes";

const ServiceItemDisplay = styled(BasicFlex)`
  flex-direction: column;
  align-items: flex-start;
  gap: .5rem;
  .main-title {

  }

  .add-info {
    font-size: 1.2rem;
    color: ${props => props.theme.colors.mildText};
  }
`;

const displayLabel = (option) => {
  return (
   <ServiceItemDisplay>
    <div className="main-title">
      {option.name}
    </div>
    <div className="add-info">
      {option.duration_in_minutes} min  &middot;  {formatCurrency(option.service_price)}
    </div>
   </ServiceItemDisplay>
  )
}

const displayWigLabel = (option) => {
    let status;
    if (option?.status === "sold") {
      status = `Sold On ${formatGenericDate(option?.last_log_date, "short")} To ${option?.customer_name}`
    } else if (option?.status === "removed") {
      status = "Returned";
    } else {
      status = "In Stock"
    }
    let info = <div>{option?.sku} &middot; <span style={{fontStyle: "italic", fontWeight: "600"}}>{status}</span></div>
    return autoCompleteDisplayLabel(option?.wig_id, info);
  }


const ServiceItem = ({ service, remove, index, sfv, stylist_id, ...props }) => {
  const { values, setFieldValue } = useFormikContext();

  const serviceUpdates = [{name: `services.${index}.duration_in_minutes`, field: "duration_in_minutes"}, {name: `services.${index}.service_price`, field: "service_price"}, {name: `services.${index}.label_color`, field: "label_color"}];

  let stylist = service?.stylist_id;

  const { data: services } = useQuery({
    queryKey: ["services", "stylists", stylist],
    queryFn: () => getServicesByStylist(stylist)
  });

  const serviceOptions = useMemo(() => (service?.service_ticket_id ? [service].concat(services?.filter(option => (option?.name !== service?.name)) ) : services), [service?.service_ticket_id, services]);

  // const serviceOptions = 

  let { stylists } = useScheduleTypes() || {};

  let wig_source = service?.wig_source;

  let disabled = stylist ? false : true;

  const { data: customerWigs} = useQuery({
    queryKey: ['inventory', values?.customer_id],
    queryFn: () => getSoldInventoryByCustomer(values?.customer_id),
    enabled: !!values?.customer_id
  })

  useEffect(() => {
    if (service?.inventory_id) {
      setFieldValue(`services.${index}.wig_source`, "inventory");
    } else {
      setFieldValue(`services.${index}.wig_source`, "external");
    }
  }, [service?.wig_id, service?.inventory_id])


  return (
    <Grid container spacing={2} sx={{marginBottom: "1rem"}}>
      <Grid size={{xs: 12}}>
        <InputLabel>Select Stylist</InputLabel>
        <AutocompleteFormik name={`services.${index}.stylist_id`} size="small" fullWidth fieldName="stylist_id" displayField="full_name" url="stylists" serverOptions={stylists} formUpdates={[{name: `services.${index}.stylist_name`, field: "full_name"}]} />
      </Grid>
      {/* <Grid size={{xs: 12}}>
          <InputLabel>Select Wig Source</InputLabel>
          <ToggleButtonComponent name={`services.${index}.wig_source`} options={[{label: "Inventory", value: "inventory"}, {label: "External", value: "external"}]} defaultValue="inventory" />
      </Grid> */}
      <Grid size={{xs: 12, md: 3}}>
        <InputLabel dense>Service</InputLabel>
        <AutocompleteFormik name={`services.${index}.name`} size="small" fullWidth fieldName="name" url="services" formUpdates={serviceUpdates} serverOptions={serviceOptions} displayLabel={displayLabel} disabled={disabled} simple={true} />
      </Grid>
      <Grid size={{xs: 12, md: 2}}>
        <InputLabel dense>Duration</InputLabel>
        <Field component={TextField} name={`services.${index}.duration_in_minutes`} size="small" fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><AccessTimeRoundedIcon color="icon" /></InputAdornment> }} disabled={disabled} />
      </Grid>
      <Grid size={{xs: 12, md: 2}}>
        <InputLabel dense>Price</InputLabel>
        <Field component={TextField} name={`services.${index}.service_price`} size="small" fullWidth InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoneyRoundedIcon color="icon" /></InputAdornment> }} disabled={disabled} />
      </Grid>
      <Grid size={{xs: 12, md: 4}}>
        <InputLabel dense>Customer Wig</InputLabel>
        <AutocompleteFormik 
          name={`services.${index}.wig_id`} 
          size="small" 
          fullWidth 
          fieldName="wig_id"
          // url="inventory" 
          serverOptions={customerWigs} 
          displayLabel={displayWigLabel} 
          disabled={disabled} 
          // forceServer={true}
          // conditionStr={`&customer_id=${values?.customer_id}`} 
          formUpdates={[
            {name: `services.${index}.inventory_id`, field: "inventory_id"},
          ]} 
          simple={true} 
        />
      </Grid>
      <Grid size={{xs: 12, md: 1}}>
        <Tooltip title="Remove Service">
        <IconButton onClick={() => remove(index)} sx={{marginTop: 2.5}}><RemoveCircleOutlineRoundedIcon color="error" /></IconButton>
        </Tooltip>
      </Grid>
      <Grid size={{xs: 12}}>
        <FastField component={TextField} name={`services.${index}.notes`} size="small" fullWidth placeholder="Add Service Notes (Internal Use Only)" multiline
        rows={3} />
      </Grid>
    </Grid>
  )
}

const ServiceList = ({ client }) => {
  const { errors, values, setFieldValue } = useFormikContext();

  const { formValues, setFormValues } = useFormData() || {};
  
  const client_name = formValues?.customer_name;

  const { stylist_id } = values;

  const theme = useTheme();

  const addService = (push) => {
    // if (!client_name) return;
    let rand = Math.random();
    push({ client_name, name: "", duration_in_minutes: "", service_price: "", wig_id: "", wig_source: "inventory", key: rand, stylist_id: stylist_id });
  }

  useEffect(() => {
    let totalDuration = values?.services?.reduce((total, service) => total + (+service.duration_in_minutes || 0), 0);
    let totalPrice = values?.services?.reduce((total, service) => total + (+service.service_price || 0), 0);
    let endTime = addMinutes(values.start_time, totalDuration);
    let mainService = values.services?.[0];
    let title = mainService?.name;
    let update_fields = {
      duration_in_minutes: totalDuration,
      subtotal: totalPrice,
      end_time: endTime,
      title: title,
    };
    if (mainService?.label_color) {
      update_fields.block_color = mainService?.label_color;
    } else {
      if (values.block_color) {
        update_fields.block_color = values.block_color;
      }
    }
    setFormValues(ov => ({ ...ov, ...update_fields }));
    setFieldValue("end_time", endTime);
  }, [values.services, values.start_time]);

  return (
    <FieldArray name="services">
      {({ push, remove }) => (
        <div>
          {values?.services?.map((service, index) => {
            return (
            <Card variant="outlined" sx={{p: 2.5, mb: "2rem", border: `1px solid ${theme.palette.secondary.soft}`, borderLeft: `3px solid ${theme.palette.secondary.soft}`}}>
              <ServiceItem service={service} index={index} key={service?.key} remove={remove} sfv={setFieldValue} stylist_id={stylist_id} />
              {/* {values?.services?.length > 1 ? <Divider sx={{my: "2rem"}} /> : null} */}
            </ Card>
          )
          })}
          {errors?.services && typeof errors?.services === "string" && <ErrorMessage error message={errors?.services} />}
          <Button onClick={() => addService(push)} startIcon={<AutoFixHighIcon />} sx={{my: 1.5}}>Add Service</Button>
        </div>
      )}
    </FieldArray>
  );
};

export default ServiceList;